import React, { useState } from 'react';
import { Button } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined, DragOutlined } from '@ant-design/icons';
import { CanvasObject } from '../types';

interface ObjectsStackProps {
    objects: CanvasObject[];
    selectedObjects: string[];
    onReorderObjects: (newObjects: CanvasObject[]) => void;
    onSelectObject: (objectId: string) => void;
    onToggleVisibility?: (objectId: string) => void;
}

export const ObjectsStack: React.FC<ObjectsStackProps> = ({
    objects,
    selectedObjects,
    onReorderObjects,
    onSelectObject,
    onToggleVisibility
}) => {
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const getObjectIcon = (type: string) => {
        switch (type) {
            case 'text': return '📝';
            case 'rectangle': return '⬜';
            case 'circle': return '⭕';
            case 'arrow': return '➡️';
            case 'image': return '🖼️';
            default: return '📦';
        }
    };

    const getObjectName = (obj: CanvasObject) => {
        switch (obj.type) {
            case 'text': return obj.text || 'Text';
            case 'image': return obj.imageName || 'Image';
            default: return obj.type.charAt(0).toUpperCase() + obj.type.slice(1);
        }
    };

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();

        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const newObjects = [...objects];
        const draggedObject = newObjects[draggedIndex];

        // Remove the dragged object
        newObjects.splice(draggedIndex, 1);

        // Calculate the correct insert position
        // If dragging down (to higher index), the index shifts by 1 after removal
        const insertIndex = draggedIndex < dropIndex ? dropIndex : dropIndex;
        newObjects.splice(insertIndex, 0, draggedObject);

        onReorderObjects(newObjects);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    // Reverse the objects array to show top layer first (last in array = top layer)
    const reversedObjects = [...objects].reverse();

    return (
        <div style={{ padding: '0 16px' }}>
            <div style={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#666',
                marginBottom: '12px',
                textAlign: 'center'
            }}>
                🗂️ LAYERS ({objects.length})
            </div>
            <div style={{
                fontSize: '10px',
                color: '#999',
                textAlign: 'center',
                marginTop: '12px',
                padding: '8px',
                background: '#f9f9f9',
                borderRadius: '4px'
            }}>
                💡 Drag objects to change layer order
            </div>

            {objects.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: '#999',
                    fontSize: '12px'
                }}>
                    No objects on canvas
                </div>
            ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {reversedObjects.map((obj, reverseIndex) => {
                        const actualIndex = objects.length - 1 - reverseIndex;
                        const isSelected = selectedObjects.includes(obj.id);
                        const isDragging = draggedIndex === actualIndex;
                        const isDragOver = dragOverIndex === actualIndex;

                        return (
                            <div
                                key={obj.id}
                                draggable
                                onDragStart={(e) => handleDragStart(e, actualIndex)}
                                onDragOver={(e) => handleDragOver(e, actualIndex)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, actualIndex)}
                                onDragEnd={handleDragEnd}
                                onClick={() => onSelectObject(obj.id)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '8px 12px',
                                    marginBottom: '4px',
                                    background: isSelected ? '#e6f7ff' : isDragOver ? '#f0f0f0' : 'white',
                                    border: isSelected ? '1px solid #1890ff' : '1px solid #e8e8e8',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    opacity: isDragging ? 0.5 : 1,
                                    transform: isDragOver ? 'translateY(-2px)' : 'none',
                                    transition: 'all 0.2s ease',
                                    fontSize: '12px'
                                }}
                            >
                                <DragOutlined
                                    style={{
                                        color: '#999',
                                        marginRight: '8px',
                                        cursor: 'grab'
                                    }}
                                />

                                <span style={{ marginRight: '8px', fontSize: '14px' }}>
                                    {getObjectIcon(obj.type)}
                                </span>

                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                        fontWeight: isSelected ? 600 : 400,
                                        color: isSelected ? '#1890ff' : '#333',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {getObjectName(obj)}
                                    </div>
                                    <div style={{
                                        fontSize: '10px',
                                        color: '#999',
                                        marginTop: '2px'
                                    }}>
                                        {Math.round(obj.position.x)}, {Math.round(obj.position.y)} • {Math.round(obj.size.width)}×{Math.round(obj.size.height)}
                                    </div>
                                </div>

                                {onToggleVisibility && (
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<EyeOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onToggleVisibility(obj.id);
                                        }}
                                        style={{
                                            minWidth: 'auto',
                                            width: '24px',
                                            height: '24px',
                                            padding: 0,
                                            color: '#999'
                                        }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}


        </div>
    );
};